import { CircularProgress, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { NAME_REGEX, NO_SCRIPT_REGEX } from "../../../../utils/regex";
import { CampaignInfo, CampaignSavedData } from "../../../../types/apiTypes";
import { useValidateCampaignDataMutation } from "../../../../api";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { toast } from "react-toastify";
import { equals } from "../../../../utils/equals";
import { ReuseInput } from "../../../ui";
import { CustomButton } from "components/ui/CustomButton";
import { CustomToast } from "../../../ui/CustomToast";

type Props = {
  initData: CampaignSavedData;
  setResultData: (data: CampaignSavedData) => void;
  nextStepHandler: () => void;
  backHandler: () => void;
};

type Errors = { [key: string]: boolean };

const validation: { [key: string]: (value: string) => boolean } = {
  company_name: (value: string) => !NAME_REGEX.test(value),
  product_abstract: (value: string) => !value || !NO_SCRIPT_REGEX.test(value),
  product_name: (value: string) => !NAME_REGEX.test(value),
};

const initialValues: CampaignInfo = {
  company_name: "",
  product_abstract: "",
  product_name: "",
};

export const ConfirmAndCustomizeStep = ({
  initData,
  setResultData,
  nextStepHandler,
  backHandler,
}: Props) => {
  const [errorMessage, setErrorMessage] = useState<CampaignInfo>(initialValues);

  const [formData, setFormData] = useState<CampaignInfo>(initialValues);

  const [error, setError] = useState<Errors>({
    company_name: false,
    product_abstract: false,
    product_name: false,
  });

  const [
    validateCampaignData,
    {
      isSuccess,
      error: validateCampaignDataError,
      isLoading: validateCampaignDataIsLoading,
    },
  ] = useValidateCampaignDataMutation();

  useEffect(() => {
    if (validateCampaignDataError) {
      if (getErrorMessage(validateCampaignDataError).fieldErrors) {
        setErrorMessage((err) => ({
          ...err,
          ...getErrorMessage(validateCampaignDataError).fieldErrors,
        }));
      } else {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(validateCampaignDataError).globalError ||
              "Something went wrong during processing. Please try again"
            }
            title={"Changes Were Not Saved"}
          />,
        );
      }
    }
  }, [validateCampaignDataError]);

  useEffect(() => {
    if (isSuccess) {
      nextStepHandler();
      setResultData({ ...initData, ...formData });
    }
  }, [isSuccess]);

  const inputChangeHandler = (field: string, value: string) => {
    setErrorMessage((prev) => ({ ...prev, [field]: "" }));
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError((prev) => ({
      ...prev,
      [field]: validation[field](value),
    }));
  };

  useEffect(() => {
    setFormData(initData);
  }, [initData]);

  const setErrorHandler = (field: string, value: boolean) => {
    setError((prev) => ({ ...prev, [field]: value }));
  };

  const submitHandler = () => {
    if (equals(formData, initData)) {
      nextStepHandler();
      return;
    }
    validateCampaignData(formData);
  };

  return (
    <Stack gap="16px" width="100%">
      <Stack gap="42px" paddingBottom="24px">
        <ReuseInput
          name="company_name"
          handleInputChange={inputChangeHandler}
          errorMessage={
            errorMessage.company_name ||
            "Must contain only letters, numbers, and basic punctuation"
          }
          value={formData.company_name || ""}
          label="Provider Company Name *"
          maxLength={100}
          placeholder="Provider Company Name"
          isError={error.company_name || !!errorMessage.company_name}
          setErrorHandler={setErrorHandler}
        />
        <ReuseInput
          name="product_name"
          handleInputChange={inputChangeHandler}
          errorMessage={
            errorMessage.product_name ||
            "Must contain only letters, numbers, and basic punctuation"
          }
          value={formData.product_name || ""}
          label="Product, Solution or Service Name *"
          maxLength={100}
          placeholder="Product, Solution or Service Name"
          isError={error.product_name || !!errorMessage.product_name}
          setErrorHandler={setErrorHandler}
        />
        <ReuseInput
          name="product_abstract"
          handleInputChange={inputChangeHandler}
          errorMessage={
            errorMessage.product_abstract ||
            "This field is required and cannot include scripts."
          }
          value={formData.product_abstract || ""}
          label="Product, Solution or Service Description *"
          maxLength={1500}
          multiline={true}
          placeholder=" e.g., INFUSE is a global high-performance demand partner delivering demand strategies,
         programs, and outcomes for the most admired B2B brands"
          isError={error.product_abstract || !!errorMessage.product_abstract}
          setErrorHandler={setErrorHandler}
          noteText={"Supports target audience and decision makers discovery"}
        />
      </Stack>
      <Stack direction="row" gap="8px">
        <CustomButton
          onClick={backHandler}
          variant="contained"
          textColor="#4C545B"
          fullWidth
          type="button"
          customColor="#F1F1F1"
          disabled={validateCampaignDataIsLoading}
        >
          Back
        </CustomButton>
        <CustomButton
          onClick={submitHandler}
          variant="contained"
          fullWidth
          type="button"
          customColor="#1C3C6C"
          disabled={
            validateCampaignDataIsLoading || Object.values(error).includes(true)
          }
        >
          Next Step{" "}
          {validateCampaignDataIsLoading && (
            <CircularProgress size={20} color="inherit" />
          )}
        </CustomButton>
      </Stack>
    </Stack>
  );
};
